import React, { useCallback, useMemo, useState } from 'react'
import styles from './SavedForLater.module.less'
import Space from 'antd/lib/space'
import Input from 'antd/lib/input'
import Flex from 'antd/lib/flex'
import TableComp from 'Components/common/Table/Table.tsx'
import Tooltip from 'antd/lib/tooltip'
import Alert from 'antd/lib/alert'
import { TablePaginationConfig } from 'antd/lib/table/interface'
import { CopyOutlined, CloseOutlined } from '@ant-design/icons'
import { useMessage } from 'shared/hooks/use-message.ts'
import Button from 'antd/lib/button'
import EyeFilled from '@ant-design/icons/lib/icons/EyeFilled'
import { ResumeIcon } from 'Components/common/Icons/Resume.tsx'
import { useNavigate, useParams } from 'react-router-dom'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'
import { getOnHoldTaskDetails, resumeTaskByUser } from 'Services/tasks.service.ts'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { NavigationSource } from 'Constants/constants.ts'
import { TableColumnsType } from 'antd/lib'
import { SavedForLaterFilterType, SearchByIdOptionType } from 'Types/Tasks.types.ts'
import { debounce } from 'lodash'
import Select from 'antd/lib/select'

export type SavedTaskType = {
    key: string
    jobId: string
    taskId: string
    submissionId: string
    comment: string
    savedAt: string
}

const SavedForLaterTasks: React.FC = () => {
    const [messageApi] = useMessage()
    const navigate = useNavigate()
    const { stepId } = useParams<{ stepId: string }>()
    const queryClient = useQueryClient()
    const { userProfile } = useUserProfile()
    const [filters, setFilters] = useState<SavedForLaterFilterType>({})
    const [searchByIdOption, setSearchByIdOption] = useState<SearchByIdOptionType>('jobId')
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    })

    const queryKey = useMemo(
        () => ['onHoldTasks', userProfile.user.user_id, stepId],
        [userProfile.user.user_id, stepId]
    )

    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () =>
            getOnHoldTaskDetails({
                user_id: userProfile.user.user_id,
                step_id: stepId!,
            }),
        enabled: !!userProfile.user.user_id && !!stepId,
        staleTime: 0,
    })

    const filterBySearchOptions = useMemo(
        () => (
            <Select
                value={searchByIdOption}
                onChange={(val: SearchByIdOptionType) => {
                    setSearchByIdOption(val)
                    setPagination(prev => ({ ...prev, current: 1 }))
                }}
                options={[
                    { label: 'Job ID', value: 'jobId' },
                    { label: 'Task ID', value: 'taskId' },
                ]}
            />
        ),
        [searchByIdOption]
    )

    const handleSearchChange = debounce((value: string) => {
        setFilters(prev => ({ ...prev, id: value || undefined }))
        setPagination(prev => ({ ...prev, current: 1 }))
    }, 500)

    const resumeMutation = useMutation({
        mutationFn: ({ submissionId }: { submissionId: string }) =>
            resumeTaskByUser({
                id: submissionId,
                user_id: userProfile.user.user_id,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey })
            messageApi.success('Task resumed')
        },
        onError: () => {
            messageApi.error('Failed to resume task')
        },
        onSettled: () => {
            navigate(`/task-queue/${stepId}?source=${NavigationSource.SAVED_TASKS}`)
        },
    })

    const handleCopyToClipboard = useCallback(
        (text: string) => {
            navigator.clipboard.writeText(text).then(() => {
                messageApi.success('Copied to clipboard')
            })
        },
        [messageApi]
    )

    const copyableTooltip = useMemo(() => {
        return (text: string) => (
            <Tooltip
                title={
                    <Flex align="center" gap={5}>
                        <span>{text}</span>
                        <CopyOutlined
                            onClick={e => {
                                e.stopPropagation()
                                handleCopyToClipboard(text)
                            }}
                        />
                    </Flex>
                }
                placement="topLeft"
            >
                <div>{text}</div>
            </Tooltip>
        )
    }, [handleCopyToClipboard])

    const columns: TableColumnsType<SavedTaskType> = useMemo(
        () => [
            {
                title: 'Job ID',
                dataIndex: 'jobId',
                render: copyableTooltip,
                width: 300,
                className: styles.tableContainerTd,
            },
            {
                title: 'Task ID',
                dataIndex: 'taskId',
                render: copyableTooltip,
                width: 300,
                className: styles.tableContainerTd,
            },
            {
                title: 'Comment',
                dataIndex: 'comment',
                className: styles.tableContainerTd,
            },
            {
                title: 'Last saved at',
                dataIndex: 'savedAt',
                width: 250,
                className: styles.tableContainerTd,
            },
            {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                    <Flex gap={10} align="center">
                        <Tooltip title="Preview task">
                            <EyeFilled
                                className={styles.actionIcon}
                                onClick={() => {
                                    window.open(
                                        `/preview-on-hold/${record.taskId}`,
                                        '_blank',
                                        'noopener noreferrer'
                                    )
                                }}
                            />
                        </Tooltip>
                        <Tooltip
                            title={
                                data?.inProgressTaskId
                                    ? 'Take action in the in-progress task to resume'
                                    : 'Resume task'
                            }
                        >
                            <ResumeIcon
                                className={styles.actionIcon}
                                onClick={() => {
                                    if (!data?.inProgressTaskId) {
                                        resumeMutation.mutate({ submissionId: record.submissionId })
                                    }
                                }}
                                style={{
                                    cursor: data?.inProgressTaskId ? 'not-allowed' : 'pointer',
                                }}
                            />
                        </Tooltip>
                    </Flex>
                ),
                width: 150,
                className: styles.tableContainerTd,
            },
        ],
        [copyableTooltip, data?.inProgressTaskId, resumeMutation]
    )

    const processedTasks = useMemo(() => {
        return data?.onHoldTasks?.map(task => ({
            key: task.taskId,
            jobId: task.jobId,
            taskId: task.taskId,
            submissionId: task.submissionId,
            comment: task.savedReason,
            savedAt: task.lastSavedAt,
        }))
    }, [data?.onHoldTasks])

    const filteredTasks = useMemo(() => {
        if (!processedTasks) return undefined

        if (!filters.id?.trim()) {
            return processedTasks
        }

        const searchTerm = filters.id.trim().toLowerCase()

        return processedTasks.filter(task => {
            if (searchByIdOption === 'jobId') {
                return task.jobId.toLowerCase().includes(searchTerm)
            }

            return task.taskId.toLowerCase().includes(searchTerm)
        })
    }, [processedTasks, filters.id, searchByIdOption])

    const paginatedTasks = useMemo(() => {
        if (!filteredTasks) return undefined

        const startIndex = (pagination.current - 1) * pagination.pageSize
        const endIndex = startIndex + pagination.pageSize
        return filteredTasks.slice(startIndex, endIndex)
    }, [filteredTasks, pagination])

    return (
        <div className={styles.container}>
            <div className={styles.headerRow}>
                <div>
                    <h2 className={styles.title}>Tasks saved for later</h2>
                    <p className={styles.subtitle}>
                        {data?.project.nickname} · {data?.step.name}
                    </p>
                </div>
                <div className={styles.closeIconWrapper}>
                    <CloseOutlined
                        className={styles.closeIcon}
                        onClick={() => navigate('/', { replace: true })}
                    />
                </div>
            </div>

            {data?.inProgressTaskId && (
                <Alert
                    message={
                        data?.onHoldTasks?.length
                            ? 'You have a task in progress. Please, open & take action so that you can resume the below saved tasks.'
                            : 'You have a task in progress. Either complete it or save the task for later.'
                    }
                    type="warning"
                    showIcon
                    action={
                        <Button
                            onClick={() =>
                                navigate(
                                    `/task-queue/${stepId}?source=${NavigationSource.SAVED_TASKS}`
                                )
                            }
                        >
                            Open task
                        </Button>
                    }
                    className={styles.alert}
                />
            )}

            <Flex className={styles.filterSection}>
                <Space>
                    <Input
                        data-testid="search-input"
                        placeholder="Search by ID"
                        className={styles['search-input']}
                        addonBefore={filterBySearchOptions}
                        onChange={e => handleSearchChange(e.target.value)}
                        allowClear
                        value={filters.id}
                    />
                </Space>
            </Flex>

            <div className={styles.tableContainer}>
                <TableComp<SavedTaskType>
                    rowKey="taskId"
                    columns={columns}
                    dataSource={paginatedTasks}
                    pagination={{
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showSizeChanger: true,
                        position: ['bottomCenter'],
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: filteredTasks?.length,
                    }}
                    onChange={(paginationConfig: TablePaginationConfig) => {
                        setPagination({
                            current: paginationConfig.current || 1,
                            pageSize: paginationConfig.pageSize || 10,
                        })
                    }}
                    loading={isLoading || resumeMutation.isPending}
                    emptyIconText="No saved tasks found"
                />
            </div>
        </div>
    )
}

export default SavedForLaterTasks
